import React from 'react';
import { PATH } from 'common/constants';
import type { NotificationOut } from 'service/notificationsService';
import type { HrisLoadSource, KnownNotification } from './types';
import { GenericNotification } from './renderers/GenericNotification';

type NotificationTypeMap = { [N in KnownNotification as N['type']]: N };

// Automated feeds (SFTP / Workday) have no interactive submitter, so they get passive
// copy with no "reload" action; an interactive customer upload (APP) gets the actionable
// "reload it" guidance. Wording mirrors the HRIS load emails
// (FileRepository._send_recipient_notifications) so the bell and the email stay in sync.
const isAutomated = (source?: HrisLoadSource) => source === 'SFTP' || source === 'WORKDAY';

// Finalize in NI-8
// Exhaustive over KnownNotification['type'], every type must have a renderer or this won't compile.
const registry: { [K in KnownNotification['type']]: (notification: NotificationTypeMap[K]) => React.ReactNode } = {
  // Allows users to build components with content from their notification payload
  'hris.load_failed': (n) => (
    <GenericNotification
      title={isAutomated(n.payload?.source) ? 'HRIS feed has errors' : 'Upload has errors'}
      description={
        isAutomated(n.payload?.source)
          ? "Your latest HRIS feed couldn't be processed."
          : "Your HRIS file couldn't be processed. Review the errors and reload it."
      }
      tag={{ color: 'error', children: 'Errors' }}
      link={{ text: 'Review errors', link: PATH.DATA_MANAGEMENT }}
    />
  ),
  'hris.load_partial': (n) => (
    <GenericNotification
      title={isAutomated(n.payload?.source) ? 'HRIS feed partially loaded' : 'Upload partially loaded'}
      description={
        isAutomated(n.payload?.source)
          ? "Your latest HRIS feed was partially loaded. Review what wasn't loaded."
          : "Your HRIS file was partially loaded. Review what wasn't loaded and reload it."
      }
      tag={{ color: 'warning', children: 'Partial' }}
      link={{ text: 'Review errors', link: PATH.DATA_MANAGEMENT }}
    />
  ),
  'hris.load_success': (n) => (
    <GenericNotification
      title={isAutomated(n.payload?.source) ? 'HRIS feed loaded' : 'Upload successful'}
      description={
        isAutomated(n.payload?.source)
          ? 'Your latest HRIS feed has been loaded.'
          : 'Your HRIS file has been loaded.'
      }
      tag={{ color: 'success', children: 'Loaded' }}
      link={{ text: 'View data', link: PATH.DATA_MANAGEMENT }}
    />
  ),
  'survey.load_success': () => (
    <GenericNotification
      title="Survey loaded successfully"
      description="Your survey data has finished loading."
    />
  ),
  'survey.load_failed': () => (
    <GenericNotification
      title="Survey load failed"
      description="Your survey data could not be loaded. Please try again."
      tag={{ color: 'error', children: 'Error' }}
    />
  ),
  'survey.field_mapping_ready': () => (
    <GenericNotification
      title="Survey field mapping ready"
      description="Review and confirm the field mapping for your survey."
      tag={{ color: 'success', children: 'Ready' }}
      link={{ text: 'Review', link: 'google.com' }}
    />
  ),
};

// Runtime list of registered types, derived from `registry` so it can't drift.
// Single source of truth for anything needing the known-type set (e.g. the dev
// notifications sandbox).
export const KNOWN_NOTIFICATION_TYPES = Object.keys(registry) as KnownNotification['type'][];

function isKnownNotification(n: NotificationOut): n is KnownNotification {
  return n.type in registry;
}

function callRegistry<K extends KnownNotification['type']>(
  key: K,
  notification: NotificationTypeMap[K]
): React.ReactNode {
  return registry[key](notification);
}

// Defers registry invocation to render time so a parent error boundary can contain throws.
function RegistryInvocation({ notification }: { notification: KnownNotification }) {
  return <>{callRegistry(notification.type, notification)}</>;
}

export function renderNotification(notification: NotificationOut): React.ReactNode | null {
  if (!isKnownNotification(notification)) {
    console.error(`Missing notification registry entry for type: ${notification.type}`);
    return null;
  }
  return <RegistryInvocation notification={notification} />;
}
